/**
 * UnifiedDiffViewer - PRODUCTION GRADE
 * Ported from glowtest_admin_new/src/components/UnifiedDiffViewer.jsx
 *
 * Industry-standard diff viewer matching VS Code / GitHub features:
 * - Word-level highlighting (shows exact characters changed)
 * - Collapse unchanged regions with configurable context
 * - Navigation buttons (next/previous change)
 * - Split view / Unified view toggle
 * - Line wrap toggle
 * - Change statistics
 *
 * @param oldValue - Original text content
 * @param newValue - Modified text content
 * @param title - Header title
 * @param action - Optional action buttons for header
 * @param leftTitle - Label for left column (default: "Original")
 * @param rightTitle - Label for right column (default: "Changes")
 * @param splitView - Initial split view state (default: true)
 * @param showDiffOnly - Initially collapse unchanged regions (default: true)
 * @param extraLinesSurroundingDiff - Context lines around changes (default: 3)
 * @param compareMethod - Diff algorithm (default: DiffMethod.WORDS)
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import {
  Box,
  FormControlLabel,
  Switch,
  Paper,
  CardHeader,
  IconButton,
  Tooltip,
  ButtonGroup,
  Button,
  Typography,
  Chip,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import { createTheme, ThemeProvider } from '@mui/material/styles';

import { DIFF_DARK, DIFF_LIGHT, type DiffColors } from '@/theme';
import { useTheme } from '@/components/theme/use-theme';

interface UnifiedDiffViewerProps {
  oldValue: string;
  newValue: string;
  title?: string;
  action?: React.ReactNode;
  leftTitle?: string;
  rightTitle?: string;
  splitView?: boolean;
  showDiffOnly?: boolean;
  extraLinesSurroundingDiff?: number;
  compareMethod?: typeof DiffMethod[keyof typeof DiffMethod];
  /** Optional container className (e.g. to control height/width from parent). */
  className?: string;
  /** Optional container style. */
  style?: React.CSSProperties;
}

const UnifiedDiffViewer: React.FC<UnifiedDiffViewerProps> = ({
  oldValue,
  newValue,
  title,
  action = null,
  leftTitle = "Original",
  rightTitle = "Changes",
  splitView: initialSplitView = true,
  showDiffOnly: initialShowDiffOnly = true,
  extraLinesSurroundingDiff = 3,
  compareMethod = DiffMethod.WORDS,
  className,
  style,
}) => {
  // Theme detection — the central theme context resolves "system" for us
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const diff = (isDark ? DIFF_DARK : DIFF_LIGHT) as DiffColors;

  // MUI components (Typography, Switch, the "Wrap" label, footer captions, buttons) read their
  // colors from a MUI theme, not raisy's Tailwind theme. Without this they default to MUI's
  // LIGHT palette (dark text) and become invisible on the dark diff surface. Drive the MUI
  // palette mode from raisy's theme so every control adapts to light/dark/system.
  const muiTheme = useMemo(
    () => createTheme({ palette: { mode: isDark ? 'dark' : 'light' } }),
    [isDark],
  );

  // View state
  const [wordWrap, setWordWrap] = useState(true);
  const [splitView, setSplitView] = useState(initialSplitView);
  const [showDiffOnly, setShowDiffOnly] = useState(initialShowDiffOnly);

  // Navigation state
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const changeMarkersRef = useRef<{ element: Element; top: number }[]>([]);

  // Ensure values are always strings
  const safeOldValue = useMemo(() =>
    typeof oldValue === 'string' ? oldValue : (oldValue ? String(oldValue) : ''),
    [oldValue]
  );
  const safeNewValue = useMemo(() =>
    typeof newValue === 'string' ? newValue : (newValue ? String(newValue) : ''),
    [newValue]
  );

  // Calculate diff statistics
  const stats = useMemo(() => {
    const oldLines = safeOldValue.split('\n');
    const newLines = safeNewValue.split('\n');

    let additions = 0;
    let deletions = 0;
    let unchanged = 0;

    const maxLen = Math.max(oldLines.length, newLines.length);
    for (let i = 0; i < maxLen; i++) {
      const oldLine = oldLines[i] || '';
      const newLine = newLines[i] || '';
      if (oldLine === newLine) {
        unchanged++;
      } else {
        if (i < oldLines.length && oldLines[i] !== '') deletions++;
        if (i < newLines.length && newLines[i] !== '') additions++;
      }
    }

    return { additions, deletions, unchanged, totalOld: oldLines.length, totalNew: newLines.length };
  }, [safeOldValue, safeNewValue]);

  // Find all change markers for navigation
  const findChangeMarkers = useCallback(() => {
    if (!containerRef.current) return [];

    const addedElements = containerRef.current.querySelectorAll('[class*="diff-added"], [class*="diff-removed"]');
    const markers = Array.from(addedElements).map(el => ({
      element: el,
      top: el.getBoundingClientRect().top + window.scrollY
    }));

    // Deduplicate by grouping nearby changes
    const grouped: typeof markers = [];
    let lastTop = -100;
    markers.forEach(marker => {
      if (marker.top - lastTop > 50) {
        grouped.push(marker);
        lastTop = marker.top;
      }
    });

    return grouped;
  }, []);

  // Update change markers when content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      changeMarkersRef.current = findChangeMarkers();
      setCurrentChangeIndex(0);
    }, 200);
    return () => clearTimeout(timer);
  }, [safeOldValue, safeNewValue, splitView, showDiffOnly, findChangeMarkers]);

  // Navigate to change
  const navigateToChange = useCallback((direction: 'next' | 'prev') => {
    const markers = changeMarkersRef.current;
    if (markers.length === 0) return;

    let newIndex: number;
    if (direction === 'next') {
      newIndex = (currentChangeIndex + 1) % markers.length;
    } else {
      newIndex = currentChangeIndex === 0 ? markers.length - 1 : currentChangeIndex - 1;
    }

    setCurrentChangeIndex(newIndex);

    const marker = markers[newIndex];
    if (marker && marker.element) {
      marker.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentChangeIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!containerRef.current) return;

      if (e.key === 'F7' || (e.ctrlKey && e.key === 'ArrowDown')) {
        e.preventDefault();
        navigateToChange('next');
      } else if (e.key === 'F6' || (e.ctrlKey && e.key === 'ArrowUp')) {
        e.preventDefault();
        navigateToChange('prev');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigateToChange]);

  // Theme-aware styles for diff viewer
  const customStyles = {
    variables: {
      dark: {
        diffViewerBackground: diff.viewerBg,
        diffViewerColor: isDark ? '#E6EDF3' : '#1F2328',
        addedBackground: diff.addedBg,
        addedColor: diff.addedColor,
        removedBackground: diff.removedBg,
        removedColor: diff.removedColor,
        wordAddedBackground: diff.addedWordBg,
        wordRemovedBackground: diff.removedWordBg,
        addedGutterBackground: diff.addedGutterBg,
        removedGutterBackground: diff.removedGutterBg,
        gutterBackground: diff.gutterBg,
        gutterBackgroundDark: diff.gutterBg,
        highlightBackground: diff.highlightBg,
        highlightGutterBackground: diff.highlightBg,
        codeFoldGutterBackground: diff.codeFoldBg,
        codeFoldBackground: diff.codeFoldBg,
        emptyLineBackground: diff.placeholderBg,
        codeFoldContentColor: diff.codeFoldColor,
      },
      light: {
        diffViewerBackground: diff.viewerBg,
        diffViewerColor: isDark ? '#E6EDF3' : '#1F2328',
        addedBackground: diff.addedBg,
        addedColor: diff.addedColor,
        removedBackground: diff.removedBg,
        removedColor: diff.removedColor,
        wordAddedBackground: diff.addedWordBg,
        wordRemovedBackground: diff.removedWordBg,
        addedGutterBackground: diff.addedGutterBg,
        removedGutterBackground: diff.removedGutterBg,
        gutterBackground: diff.gutterBg,
        gutterBackgroundDark: diff.gutterBg,
        highlightBackground: diff.highlightBg,
        highlightGutterBackground: diff.highlightBg,
        codeFoldGutterBackground: diff.codeFoldBg,
        codeFoldBackground: diff.codeFoldBg,
        emptyLineBackground: diff.placeholderBg,
        codeFoldContentColor: diff.codeFoldColor,
      },
    },
    line: {
      padding: '4px 8px',
      '&:hover': {
        background: diff.codeFoldBg,
      },
    },
    contentText: {
      fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
      fontSize: '13px',
      lineHeight: '1.5',
    },
    gutter: {
      minWidth: '40px',
      padding: '0 8px',
      '&:hover': {
        cursor: 'pointer',
        background: diff.codeFoldBg,
      },
    },
    codeFold: {
      backgroundColor: diff.codeFoldBg,
      color: diff.codeFoldColor,
      fontSize: '12px',
    },
    codeFoldContent: {
      color: diff.codeFoldColor,
    },
  };

  const totalChanges = changeMarkersRef.current.length;

  return (
    <ThemeProvider theme={muiTheme}>
    <Paper variant="outlined" ref={containerRef} sx={{ bgcolor: diff.viewerBg, display: 'flex', flexDirection: 'column', height: '100%', ...style }} className={className}>
      {/* Header with title and controls */}
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '0.95rem' }}>{title || 'Diff Viewer'}</Typography>
            {/* Change statistics */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                size="small"
                label={`+${stats.additions}`}
                sx={{ bgcolor: diff.addedBg, color: diff.addedColor, fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`-${stats.deletions}`}
                sx={{ bgcolor: diff.removedBg, color: diff.removedColor, fontWeight: 600 }}
              />
            </Box>
          </Box>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Navigation controls */}
            <ButtonGroup size="small" variant="outlined">
              <Tooltip title="Previous change (Ctrl+Up)">
                <IconButton
                  size="small"
                  onClick={() => navigateToChange('prev')}
                  disabled={totalChanges === 0}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button size="small" disabled sx={{ minWidth: 50, cursor: 'default' }}>
                {totalChanges > 0 ? `${currentChangeIndex + 1}/${totalChanges}` : '0/0'}
              </Button>
              <Tooltip title="Next change (Ctrl+Down)">
                <IconButton
                  size="small"
                  onClick={() => navigateToChange('next')}
                  disabled={totalChanges === 0}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ButtonGroup>

            {/* View toggles */}
            <Tooltip title={splitView ? "Unified view" : "Split view"}>
              <IconButton size="small" onClick={() => setSplitView(!splitView)}>
                {splitView ? <ViewStreamIcon fontSize="small" /> : <ViewColumnIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip title={showDiffOnly ? "Show all lines" : "Collapse unchanged"}>
              <IconButton size="small" onClick={() => setShowDiffOnly(!showDiffOnly)}>
                {showDiffOnly ? <UnfoldMoreIcon fontSize="small" /> : <UnfoldLessIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* Word wrap toggle - only for split view */}
            {splitView && (
              <FormControlLabel
                control={
                  <Switch
                    checked={wordWrap}
                    onChange={(e) => setWordWrap(e.target.checked)}
                    size="small"
                  />
                }
                label="Wrap"
                sx={{ ml: 1, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            )}

            {/* Custom action buttons */}
            {action}
          </Box>
        }
        titleTypographyProps={{ variant: 'h6' }}
        sx={{ pb: 0 }}
      />

      {/* Diff viewer */}
      <Box sx={{ px: 1, pb: 1, overflow: 'auto', flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            maxWidth: '100%',
            width: '100%',
            '& .react-diff-viewer': {
              width: '100%',
              maxWidth: '100%',
            },
            '& .react-diff-viewer table': {
              width: '100%',
              tableLayout: 'fixed',
              borderCollapse: 'separate',
              borderSpacing: 0,
            },
            '& .react-diff-viewer tbody tr td': {
              width: splitView ? '50%' : '100%',
              minWidth: splitView ? '200px' : 'auto',
              wordBreak: 'break-word',
              whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
              verticalAlign: 'top',
              overflow: wordWrap ? 'visible' : 'auto',
            },
            '& .react-diff-viewer tbody tr td pre': {
              wordBreak: wordWrap ? 'break-all' : 'normal',
              whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
              overflow: wordWrap ? 'visible' : 'auto',
              maxWidth: '100%',
              margin: 0,
              padding: '4px 8px',
            },
            '& .current-change': {
              outline: `2px solid ${diff.codeFoldColor}`,
              outlineOffset: '-2px',
            },
          }}
        >
          <ReactDiffViewer
            oldValue={safeOldValue}
            newValue={safeNewValue}
            splitView={splitView}
            compareMethod={compareMethod}
            showDiffOnly={showDiffOnly}
            extraLinesSurroundingDiff={extraLinesSurroundingDiff}
            disableWordDiff={false}
            hideLineNumbers={false}
            useDarkTheme={isDark}
            leftTitle={leftTitle}
            rightTitle={rightTitle}
            styles={customStyles}
            codeFoldMessageRenderer={(totalFoldedLines: number) => (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: diff.codeFoldColor,
                fontSize: '12px',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' }
              }}>
                <UnfoldMoreIcon fontSize="small" />
                Expand {totalFoldedLines} unchanged lines
              </Box>
            )}
          />
        </Box>
      </Box>

      {/* Footer with keyboard shortcuts hint */}
      <Box sx={{
        px: 2,
        py: 0.5,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: diff.gutterBg,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="caption" color="text.secondary">
          Navigate: Ctrl+Up/Down or F6/F7
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {stats.totalOld} lines (old) / {stats.totalNew} lines (new)
        </Typography>
      </Box>
    </Paper>
    </ThemeProvider>
  );
};

export default UnifiedDiffViewer;
